import { useEffect, useState } from "react";
import { SearchField } from "@breakingthelines/design-system";
import clsx from "clsx";

import { searchEntities, type EntityFacts } from "@/lib/admin-api";
import { entityKindSpec, safeMediaUrl } from "@/lib/entity-assets";

/**
 * The name search this page shipped without.
 *
 * SEARCHES ACROSS EVERY KIND, ALWAYS. This used to send the Kind select's
 * current value as the search's `type` filter, so a query silently narrowed to
 * whatever kind the dropdown happened to be on - "premier league" against the
 * default "Club" returns nothing, because Premier League is a
 * competition and the search never looked outside teams. The BFF and
 * identity-server already treat an absent type as unfiltered, so the fix is to
 * stop sending one; the Kind select is left as a filter on the loaded editor,
 * not on this search. Each row shows the entity's own kind, and picking a
 * result sets the Kind select to match it (entity-editor.tsx's onSelect).
 *
 * Interaction copied from the Authorized Publishers combobox and the advertiser
 * principal search so the estate's three searches behave identically: debounce,
 * arrow keys, Enter to pick, Escape to close.
 *
 * ONE THING SEARCH CANNOT REACH, and the page keeps its canonical-id field for
 * it: identity-server matches the BAKED name index, which no admin override
 * rewrites, so a club renamed by an override is found under its old name and
 * not its new one. (A stadium is also never a result, because the deployed
 * identity artifact holds no venues at all - but that is no longer a reason to
 * hide the search box, since it searches every other kind regardless of which
 * one the Kind select is on.)
 *
 * NO NON-BTL URL IS EVER RENDERED. The thumbnail comes from identity's own
 * image_url, which is a value out of a data pipeline whose upstream is
 * Wikimedia. safeMediaUrl runs it through the design-system's own
 * BTL-CDN predicate and returns null for anything else, and a null draws the
 * monogram instead. An admin surface hotlinking a third party leaks the
 * admin's IP and renders an image nobody at BTL controls.
 */

const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 10;

function useDebouncedValue<T>(value: T, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

/** Two letters off the name, for a result with no renderable image. */
function monogram(name: string, entityId: string): string {
  const source = name.trim() || entityId.replace(/^btl_football_[a-z]+_/, "");
  const words = source.split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[1][0] : source.slice(0, 2);
  return letters.toUpperCase();
}

export interface EntityPickerProps {
  onSelect: (entity: EntityFacts) => void;
}

export function EntityPicker({ onSelect }: EntityPickerProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<EntityFacts[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);

  const debounced = useDebouncedValue(query.trim());
  const listboxId = "entity-picker-listbox";

  useEffect(() => {
    if (debounced.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setActiveIndex(-1);
      setSearched(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    // No `type` sent: this searches every kind, not whatever the Kind select
    // happens to be on. See the file-level comment for why that matters.
    searchEntities({ query: debounced, limit: RESULT_LIMIT })
      .then((response) => {
        if (cancelled) return;
        setResults(response.results ?? []);
        setActiveIndex(-1);
        setSearched(true);
        setOpen(true);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setResults([]);
        setSearched(false);
        setError(err.message || "Search failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debounced]);

  function pick(entity: EntityFacts) {
    onSelect(entity);
    setQuery("");
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    setSearched(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!open || results.length === 0) return;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % results.length);
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((index) => (index <= 0 ? results.length - 1 : index - 1));
        break;
      case "Enter":
        if (activeIndex >= 0 && activeIndex < results.length) {
          event.preventDefault();
          pick(results[activeIndex]);
        }
        break;
      default:
        break;
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <label className="text-xs font-semibold text-white" htmlFor="entity-picker-input">
        Find an entity
      </label>

      <div className="relative min-w-0">
        <SearchField
          id="entity-picker-input"
          label="Search entities"
          placeholder="Name or nickname"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
        />

        {open && results.length > 0 ? (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Search results"
            className="glass-popover border-border rounded-btl-sm absolute top-full right-0 left-0 z-30 mt-1 max-h-72 list-none overflow-x-hidden overflow-y-auto border p-1"
          >
            {results.map((entity, index) => {
              const thumbnail = safeMediaUrl(entity.imageUrl);
              return (
                <li key={entity.entityId} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    className={clsx(
                      "rounded-btl-sm flex w-full min-w-0 cursor-pointer items-center gap-3 border-0 p-2 text-left",
                      index === activeIndex ? "bg-grey-100" : "bg-transparent",
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => pick(entity)}
                  >
                    <span className="border-border flex size-9 shrink-0 items-center justify-center overflow-hidden rounded border bg-black">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt=""
                          className="size-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-muted-foreground text-[10px] font-semibold">
                          {monogram(entity.name, entity.entityId)}
                        </span>
                      )}
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm text-white">
                        {entity.name || entity.entityId}
                      </span>
                      <span className="text-muted-foreground truncate font-mono text-[10px]">
                        {entityKindSpec(entity.type).label} · {entity.entityId}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {loading ? <p className="text-muted-foreground m-0 text-xs">Searching</p> : null}
      {error ? <p className="text-destructive m-0 text-xs">{error}</p> : null}
      {!loading && !error && searched && results.length === 0 ? (
        <p className="text-muted-foreground m-0 text-xs leading-relaxed">
          No matches. A corrected name is still found under its original.
        </p>
      ) : null}
    </div>
  );
}
